import { useForm } from "react-hook-form";
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Link, useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { CloudUpload } from "lucide-react";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import { useAuthStore } from "../../store/authStore";
import { getErrorMessage } from "../../lib/apiClient";
import { loginRequest, verifyTwoFactor } from "./authApi";
import AuthShell from "./AuthShell";
import GoogleButton from "./GoogleButton";

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const setSession = useAuthStore((s) => s.setSession);
  const from = location.state?.from?.pathname || "/files";
  // Set by the Google callback when the account has two-step turned on.
  const [challengeToken, setChallengeToken] = useState(
    location.state?.challengeToken || "",
  );
  const [twoFactorCode, setTwoFactorCode] = useState("");

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ defaultValues: { email: "", password: "" } });

  const mutation = useMutation({
    mutationFn: loginRequest,
    onSuccess: (data) => {
      if (data.twoFactorRequired) {
        setChallengeToken(data.challengeToken);
        return;
      }
      setSession(data);
      toast.success(`Welcome back, ${data.user.displayName}`);
      // Super admins live in the portal, never the user app.
      navigate(data.user.role === "super_admin" ? "/admin" : from, {
        replace: true,
      });
    },
    onError: (error) => toast.error(getErrorMessage(error, "Login failed")),
  });

  const twoFactorMutation = useMutation({
    mutationFn: () => verifyTwoFactor(challengeToken, twoFactorCode),
    onSuccess: (data) => {
      setSession(data);
      toast.success(`Welcome back, ${data.user.displayName}`);
      navigate(data.user.role === "super_admin" ? "/admin" : from, {
        replace: true,
      });
    },
    onError: (error) =>
      toast.error(getErrorMessage(error, "Code could not be verified")),
  });

  return (
    <AuthShell
      icon={<CloudUpload className="h-7 w-7 text-brand-600" />}
      title="Sign in to MyDocUploads"
      subtitle="Your files, anywhere."
    >
      {challengeToken ? (
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            twoFactorMutation.mutate();
          }}
        >
          <Input
            label="Authenticator or recovery code"
            autoComplete="one-time-code"
            value={twoFactorCode}
            onChange={(event) => setTwoFactorCode(event.target.value)}
          />
          <Button
            type="submit"
            className="w-full"
            loading={twoFactorMutation.isPending}
          >
            Verify and sign in
          </Button>
          <button
            type="button"
            className="w-full text-sm text-slate-500 hover:text-brand-700"
            onClick={() => {
              setChallengeToken("");
              setTwoFactorCode("");
            }}
          >
            Back to password
          </button>
        </form>
      ) : (
        <form
          className="space-y-4"
          onSubmit={handleSubmit((v) => mutation.mutate(v))}
          noValidate
        >
          <Input
            label="Email"
            type="email"
            autoComplete="email"
            error={errors.email?.message}
            {...register("email", { required: "Email is required" })}
          />
          <Input
            label="Password"
            type="password"
            autoComplete="current-password"
            error={errors.password?.message}
            {...register("password", { required: "Password is required" })}
          />
          <Button type="submit" className="w-full" loading={mutation.isPending}>
            Sign in
          </Button>
        </form>
      )}
      {!challengeToken ? <GoogleButton /> : null}
      <p className="mt-3 text-center text-sm">
        <Link
          to="/forgot-password"
          className="text-slate-500 hover:text-brand-700"
        >
          Forgot your password?
        </Link>
      </p>
      <p className="mt-6 text-center text-sm text-slate-500">
        No account?{" "}
        <Link
          to="/register"
          state={location.state}
          className="font-medium text-brand-600 hover:text-brand-700"
        >
          Create one
        </Link>
      </p>
    </AuthShell>
  );
}
